import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import { PublicShell } from '../../app/PublicShell.js';
import {
  ActionButton,
  ErrorState,
  Form,
  TextField,
} from '../../components/ui/index.js';

/**
 * Sign-in page.
 *
 * The laboratory's name is shown before sign-in, from a public endpoint that
 * returns a name and nothing else — no user list, no count, and no way to test
 * whether an address is registered. The name, like every other laboratory
 * detail, comes from that organisation's settings; nothing about a particular
 * laboratory is written into this component.
 *
 * A failed sign-in shows one message for both a wrong password and an unknown
 * address, because the server deliberately answers identically. Distinguishing
 * them in the interface would reintroduce the account-enumeration hole the API
 * closed.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { user, signIn, isLoading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) return null;
  if (user) return <Navigate to="/profile" replace />;

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      navigate('/profile', { replace: true });
    } catch (caught) {
      // Rate limiting gets its own message, because "wrong password" would be
      // a lie: the credentials were never checked.
      if (caught instanceof ApiError && caught.code === 'RATE_LIMITED') {
        setError(new ApiError('RATE_LIMITED_LOGIN', 429, ''));
      } else if (caught instanceof ApiError && caught.status >= 500) {
        // A server failure is not a wrong password either: say the problem is
        // on the server, so the person does not keep retyping their details.
        setError(new ApiError('LOGIN_SERVER_ERROR', caught.status, ''));
      } else {
        setError(caught);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicShell subtitle={t('auth.signInSubtitle')}>
      <Form onSubmit={handleSubmit} isSubmitting={isSubmitting}>
        <TextField
          type="email"
          name="email"
          label={t('auth.email')}
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={setEmail}
          autoComplete="username"
          isRequired
        />

        <TextField
          type="password"
          name="password"
          label={t('auth.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          isRequired
        />

        {error ? <ErrorState error={error} /> : null}

        <ActionButton
          priority="primary"
          type="submit"
          isLoading={isSubmitting}
          isDisabled={!email || !password}
          fullWidth
        >
          {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
        </ActionButton>

        <Link
          to="/forgot-password"
          className="rounded-sm text-center text-body text-primary-800 hover:underline"
        >
          {t('auth.forgotPassword')}
        </Link>
      </Form>
    </PublicShell>
  );
}
